import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { Box, Button, ColorScheme, Grid, Modal, Text } from '@hivewatch/dark'

import { CAMERA_SECRET_TEMPLATE } from '@/features/customer-secret-provider/constants/constants'
import { formatSecretTemplate } from '@/features/customer-secret-provider/utils/formatSecretTemplate'
import { useUnlinkCustomerSecretProviderMutation } from '@/graphql/generated/hooks'
import { CredentialLinkEntityType } from '@/graphql/generated/schemas'
import { MODAL_Z_INDEX } from '@/styles/theme'
import { tVar } from '@/utils/i18n'

import { DarkEditDeviceSection } from '../../dark/DarkEditDeviceSection'
import { DarkLinkSecretProviderModal } from './DarkLinkSecretProviderModal'

interface DarkCameraSecretProviderSectionIProps {
  colorScheme: ColorScheme
  cameraId: string
  customerSecretProvider?: {
    secretArn?: string | null
    node: { id: string; providerName: string }
  } | null
}

export const DarkCameraSecretProviderSection = ({
  colorScheme,
  cameraId,
  customerSecretProvider,
}: DarkCameraSecretProviderSectionIProps) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts()
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  const [isUnlinkOpen, setIsUnlinkOpen] = useState(false)
  const [unlinkCustomerSecretProvider, { loading: isLoadingUnlink }] =
    useUnlinkCustomerSecretProviderMutation()

  const onUnlink = async () => {
    try {
      await unlinkCustomerSecretProvider({
        variables: {
          input: {
            entityId: cameraId,
            entityType: CredentialLinkEntityType.Camera,
          },
        },
        refetchQueries: ['DeviceCameraEdit'],
      })
      setIsUnlinkOpen(false)
      showSuccess(t('devices:crud_secret_remove_success'))
    } catch (error) {
      showError(t('devices:crud_secret_remove_error'))
      Sentry.captureException(
        error ?? new Error(t('devices:crud_secret_remove_error'))
      )
    }
  }

  return (
    <>
      <DarkEditDeviceSection
        actions={
          <Button
            colorScheme={colorScheme}
            onClick={() =>
              customerSecretProvider?.node
                ? setIsUnlinkOpen(true)
                : setIsLinkOpen(true)
            }
            variant='secondary'
          >
            {customerSecretProvider?.node
              ? t('devices:crud_secret_remove_button')
              : t('devices:crud_secret_add_button')}
          </Button>
        }
        colorScheme={colorScheme}
        description={t('devices:crud_secret_associate_description')}
        title={t('devices:crud_secret_label')}
      >
        <Grid
          gap='16px'
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
        >
          <Box>
            <Text
              fontSize='11px'
              fontWeight='600'
              opacity={0.7}
              textTransform='uppercase'
            >
              {t('devices:crud_secret_label')}
            </Text>
            <Text fontSize='14px' mt='4px'>
              {customerSecretProvider?.node?.providerName ?? '-'}
            </Text>
          </Box>
          {customerSecretProvider?.secretArn && (
            <Box>
              <Text
                fontSize='11px'
                fontWeight='600'
                opacity={0.7}
                textTransform='uppercase'
              >
                {t('devices:crud_secret_arn_label')}
              </Text>
              <Text fontSize='14px' mt='4px'>
                {customerSecretProvider.secretArn}
              </Text>
            </Box>
          )}
        </Grid>
      </DarkEditDeviceSection>

      <Modal
        closeOnEsc={!isLoadingUnlink}
        closeOnOverlayClick={!isLoadingUnlink}
        colorScheme={colorScheme}
        isCloseHidden={isLoadingUnlink}
        isOpen={isUnlinkOpen}
        isSaveDestructive
        isSaveLoading={isLoadingUnlink}
        onCancel={() => setIsUnlinkOpen(false)}
        onClose={() => setIsUnlinkOpen(false)}
        onSave={onUnlink}
        saveButtonLabel={t('common:remove')}
        showCancelButton={!isLoadingUnlink}
        size='compact'
        title={t('devices:crud_secret_remove_button')}
        zIndex={MODAL_Z_INDEX + 1}
      >
        <Box>
          {tVar(t, 'devices:crud_secret_remove_confirm', {
            provider: customerSecretProvider?.node?.providerName ?? '',
          })}
        </Box>
      </Modal>

      {isLinkOpen && (
        <DarkLinkSecretProviderModal
          colorScheme={colorScheme}
          entityId={cameraId}
          entityType={CredentialLinkEntityType.Camera}
          isOpen={isLinkOpen}
          onClose={() => setIsLinkOpen(false)}
          refetchQueryName='DeviceCameraEdit'
          secretTemplate={formatSecretTemplate(CAMERA_SECRET_TEMPLATE)}
        />
      )}
    </>
  )
}
